import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCandidateById, getReviewsByTarget } from '../services/api';
import Loading from '../components/Loading';

const CandidateProfilePage = () => {
  const { candidateId } = useParams();
  const [candidate, setCandidate] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCandidateData = async () => {
      setLoading(true);

      // Load candidate details
      const candidateResult = await getCandidateById(candidateId);
      if (candidateResult.success && candidateResult.candidate) {
        setCandidate(candidateResult.candidate);
      }

      // Load reviews
      const reviewsResult = await getReviewsByTarget(candidateId);
      if (reviewsResult.success) {
        setReviews(reviewsResult.reviews);
      }

      setLoading(false);
    };

    if (candidateId) {
      loadCandidateData();
    }
  }, [candidateId]);

  if (loading) {
    return <Loading message="Loading candidate profile..." />;
  }

  if (!candidate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="text-center bg-white p-8 rounded-xl shadow-xl">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Candidate Not Found</h2>
          <Link to="/dashboard" className="text-blue-600 hover:underline font-semibold">
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 'N/A';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Candidate Header */}
        <div className="bg-gradient-to-r from-white to-blue-50 rounded-2xl shadow-2xl p-8 mb-6 border-t-4 border-blue-500">
          <div className="flex flex-col md:flex-row items-start md:space-x-8">
            <div className="relative mb-4 md:mb-0">
              <img
                src={candidate.profileImage}
                alt={candidate.name}
                className="w-40 h-40 rounded-2xl object-cover border-4 border-blue-500 shadow-xl"
              />
              <div className="absolute -bottom-2 -right-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                ✓ Verified
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-col md:flex-row items-start md:justify-between mb-3">
                <div>
                  <h1 className="text-4xl font-bold text-gray-800 mb-2">
                    {candidate.name}
                  </h1>
                  <p className="text-xl text-blue-600 font-semibold mb-3">{candidate.title}</p>
                </div>
                <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-4 py-3 rounded-xl shadow-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-2">⭐</span>
                    <div>
                      <p className="text-2xl font-bold">{averageRating}</p>
                      <p className="text-xs">{reviews.length} reviews</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="flex items-center bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-medium">
                  📍 {candidate.location}
                </span>
                <span className="flex items-center bg-purple-100 text-purple-700 px-4 py-2 rounded-full font-medium">
                  💼 {candidate.experience}
                </span>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg">{candidate.bio}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Skills Section */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-l-4 border-green-500">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="bg-green-500 text-white p-2 rounded-lg mr-3">🎯</span>
                Skills & Expertise
              </h2>
              <div className="flex flex-wrap gap-3">
                {candidate.skills && candidate.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 px-5 py-3 rounded-full text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                  >
                    ✓ {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Experience & Education */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-l-4 border-purple-500">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="bg-purple-500 text-white p-2 rounded-lg mr-3">📚</span>
                Experience & Education
              </h2>
              <div className="space-y-5">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-5 rounded-xl">
                  <h3 className="font-bold text-gray-800 mb-2 flex items-center">
                    <span className="mr-2">💼</span> Experience
                  </h3>
                  <p className="text-gray-700 font-medium">{candidate.experience} of professional experience</p>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl">
                  <h3 className="font-bold text-gray-800 mb-2 flex items-center">
                    <span className="mr-2">🎓</span> Education
                  </h3>
                  <p className="text-gray-700 font-medium">{candidate.education}</p>
                </div>
              </div>
            </div>

            {/* Reviews Section */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-l-4 border-yellow-500">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="bg-yellow-500 text-white p-2 rounded-lg mr-3">⭐</span>
                Reviews ({reviews.length})
              </h2>

              {reviews.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <div className="text-5xl mb-3">💬</div>
                  <p className="text-gray-500 font-medium">No reviews yet</p>
                  <p className="text-sm text-gray-400">Be the first to leave a review!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviews.map(review => (
                    <div key={review.id} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-5 border border-gray-200 hover:shadow-md transition-all">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="font-bold text-gray-800 text-lg">
                            {review.reviewerName}
                          </span>
                          <span className="text-gray-500 text-sm ml-3">
                            {review.date}
                          </span>
                        </div>
                        <div className="flex items-center bg-yellow-100 px-3 py-1 rounded-full">
                          {[...Array(5)].map((_, i) => (
                            <span
                              key={i}
                              className={`text-lg ${
                                i < review.rating ? 'text-yellow-500' : 'text-gray-300'
                              }`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}

              <Link
                to={`/reviews/${candidateId}`}
                className="mt-5 w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-3 rounded-xl font-bold hover:from-yellow-600 hover:to-orange-600 transition-all duration-300 shadow-md hover:shadow-lg inline-block text-center"
              >
                ✍️ Write a Review
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-blue-500">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-2xl mr-2">📧</span>
                Contact Information
              </h3>
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 font-semibold mb-1">Email</p>
                  <p className="text-gray-800 font-medium">{candidate.email}</p>
                </div>
                {candidate.phone && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 font-semibold mb-1">Phone</p>
                    <p className="text-gray-800 font-medium">{candidate.phone}</p>
                  </div>
                )}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 font-semibold mb-1">Location</p>
                  <p className="text-gray-800 font-medium">{candidate.location}</p>
                </div>
              </div>
              <button className="w-full mt-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-bold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105">
                📩 Contact Candidate
              </button>
            </div>

            {/* Availability */}
            <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-green-500">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-2xl mr-2">📅</span>
                Availability
              </h3>
              <div className="bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-400 rounded-xl p-5 text-center">
                <div className="text-3xl mb-2">✓</div>
                <p className="text-green-800 font-bold text-lg">{candidate.availability}</p>
              </div>
            </div>

            {/* Salary Expectations */}
            {candidate.expectedSalary && (
              <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-yellow-500">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">💰</span>
                  Salary Expectations
                </h3>
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-5 text-center">
                  <p className="text-gray-800 font-bold text-xl">
                    {candidate.expectedSalary}
                  </p>
                </div>
              </div>
            )}

            {/* Resume */}
            {candidate.resume && (
              <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-indigo-500">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">📄</span>
                  Resume
                </h3>
                <a
                  href={candidate.resume}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-xl hover:from-indigo-700 hover:to-purple-700 inline-block text-center font-bold transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  📥 Download Resume
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateProfilePage;
